import {Fragment, useMemo} from "react";
import {PricingPlan} from "@/components/pricing/PricingPlan";
import {Span} from "@/components/common/Span";
import {Accent} from "@/components/common/Accent";
import {Emp} from "@/components/common/Emp";
import {PlanKinds, PlanTypes} from "@/app/lib/pricing/types";
import {
    FoundationPlanMonthlyPrice,
    FoundationPlanName,
    FoundationPlanYearlyPrice,
    FoundationPlusPlanMonthlyPrice,
    FoundationPlusPlanYearlyPrice,
    ProPlanMonthlyPrice,
    ProPlanName,
    ProPlanYearlyPrice,
    ProPlusPlanMonthlyPrice,
    ProPlusPlanYearlyPrice,
} from "@/app/lib/pricing/const";
import {Anchor, Divider, Stack} from "@mantine/core";
import {usePrimaryColor} from "@/components/common/hooks/useColors";
import store from "@/app/api/store/paddle-stores";
import {ccyPrice} from "@/components/pricing/price-utils";

export function usePricingPlans() {
    const featSize = 'lg';
    const primaryColor = usePrimaryColor();

    const result = useMemo(() => {
        const foundation: PricingPlan = {
            id: PlanTypes.foundation,
            kind: PlanKinds.acoustic,
            name: FoundationPlanName,
            audience: <Span>Best for <Accent>beginner</Accent> and <Accent>intermediate</Accent> drummers</Span>,
            monthlyPriceId: store.products.foundation.acoustic.monthly.id,
            annualPriceId: store.products.foundation.acoustic.yearly.id,
            // default prices
            monthlyPrice: ccyPrice(FoundationPlanMonthlyPrice),
            annualPrice: ccyPrice(FoundationPlanYearlyPrice),
            features: <Fragment>
                <Span fz={featSize}><Emp>Straight backbeat</Emp> workouts for Rock,
                    {' '}Funk, Pop, R&amp;B, Gospel, Fusion, Country, Metal, Smooth Jazz, and more.
                </Span>
                <Span fz={featSize}><Emp>Swung backbeat</Emp> workouts for Blues,
                    {' '}Ballads, Hip-Hop, R&amp;B, Gospel, and more.</Span>
                <Span fz={featSize}>
                    <Emp>270+</Emp> superb drumless music tracks to enjoy your practice.
                </Span>
            </Fragment>,
            stats: {
                ostinatos: 43 + 29,
                exercises: 1498 + 623,
                variations: 18474 + 1138, // TODO: update
                tracks: 276,
            }
        };

        const foundationPlus: PricingPlan = {
            id: PlanTypes.foundation,
            kind: PlanKinds.electronic,
            name: FoundationPlanName,
            audience: <Span>Best for <Accent>beginner</Accent> and <Accent>intermediate</Accent> drummers
{/*
                <br /><Span c={primaryColor}>with electronic drums<sup>**</sup></Span>, who want to improve
                faster
*/}
            </Span>,
            monthlyPriceId: store.products.foundation.electronic.monthly.id,
            annualPriceId: store.products.foundation.electronic.yearly.id,
            // default prices
            monthlyPrice: ccyPrice(FoundationPlusPlanMonthlyPrice),
            annualPrice: ccyPrice(FoundationPlusPlanYearlyPrice),
            features: <Fragment>
                <Span fz={featSize}
                      style={{ textDecorationLine: 'underline', textDecorationStyle: 'dotted', textDecorationThickness: 1.5, textDecorationColor: primaryColor }}><Emp>Real-time feedback</Emp> and after-practice <Emp>analysis</Emp> on
                    {' '}your <Accent>timing</Accent>, <Accent>dynamics</Accent>, <Accent>precision</Accent>,
                    {' '}and <Accent>unisons</Accent>.
                </Span>
                <Span fz={featSize}><Emp>Straight backbeat</Emp> workouts for Rock,
                    {' '}Funk, Pop, R&amp;B, Gospel, Fusion, Country, Metal, Smooth Jazz, and more.
                </Span>
                <Span fz={featSize}><Emp>Swung backbeat</Emp> workouts for Blues,
                    {' '}Ballads, Hip-Hop, R&amp;B, Gospel, and more.</Span>
                <Span fz={featSize}>
                    <Emp>270+</Emp> superb drumless music tracks to enjoy your practice.
                </Span>
                <Stack gap={'xs'}>
                    <Divider/>
                    <Span fz={'sm'}><Span c={primaryColor}>** Live feedback</Span> works on devices with a browser supporting
                        {' '}the WebMIDI standard. Please read
                        {' '}<Anchor fz={'inherit'} href={'/faq#what-do-i-need-to-use-the-live-feedback-feature'} target="_blank" rel="noreferrer">
                            What do I need to use the Live feedback feature?</Anchor> in the FAQ <Emp>before</Emp> you purchase a subscription.
                    </Span>
                </Stack>
            </Fragment>,
            stats: {
                ostinatos: 43 + 29,
                exercises: 1498 + 623,
                variations: 18474 + 1138, // TODO: update
                tracks: 276,
            }
        };

        const pro: PricingPlan = {
            id: PlanTypes.pro,
            kind: PlanKinds.acoustic,
            name: ProPlanName,
            // tag: 'Most comprehensive',
            audience: <Span>Best for <Accent>advanced</Accent> and <Accent>professional</Accent> drummers</Span>,
            monthlyPriceId: store.products.pro.acoustic.monthly.id,
            annualPriceId: store.products.pro.acoustic.yearly.id,
            // default prices
            monthlyPrice: ccyPrice(ProPlanMonthlyPrice),
            annualPrice: ccyPrice(ProPlanYearlyPrice),
            features: <Fragment>
                <Span fz={featSize}>Everything in the Foundation plan, plus:</Span>
                <Span fz={featSize}>
                    {' '}<Emp>Swing jazz</Emp> workouts
                    {' '}for Jazz, Fusion, Bebop, and Big Band.</Span>
                <Span fz={featSize}><Emp>World rhythms</Emp> workouts for Samba,
                    {' '}Songo, Mambo, Salsa (Cascara, Campana), Cha-Cha, Bossa Nova, Bembe,
                    {' '}Conga, Mozambique, Caribbean, Fusion, and more.</Span>
                <Span fz={featSize}><Emp>Linear</Emp> pattern workouts, plus <Emp>grooves &amp; fills studio</Emp>
                    {' '}where you can create <Emp>millions</Emp> of grooves &amp; fills with billions of variations.
                </Span>
                <Span fz={featSize}>
                    <Emp>300+</Emp> superb drumless music tracks to enjoy your practice.
                </Span>
            </Fragment>,
            stats: {
                ostinatos: foundation.stats!.ostinatos + 39 + 70,
                exercises: foundation.stats!.exercises + 1995 + 3730,
                variations: foundation.stats!.variations + 1138 + 2480, // TODO: update
                tracks: 303,
            }
        };

        const proPlus: PricingPlan = {
            id: PlanTypes.pro,
            kind: PlanKinds.electronic,
            name: ProPlanName,
            // tag: 'Most comprehensive',
            audience: <Span>Best for <Accent>advanced</Accent> and <Accent>professional</Accent> drummers
{/*
                <br /><Span c={primaryColor}>with electronic drums<sup>**</sup></Span>, who want to improve
                faster
*/}
            </Span>,
            monthlyPriceId: store.products.pro.electronic.monthly.id,
            annualPriceId: store.products.pro.electronic.yearly.id,
            // default prices
            monthlyPrice: ccyPrice(ProPlusPlanMonthlyPrice),
            annualPrice: ccyPrice(ProPlusPlanYearlyPrice),
            features: <Fragment>
                <Span fz={featSize}>Everything in the Foundation plan, plus:</Span>
                <Span fz={featSize}
                      style={{ textDecorationLine: 'underline', textDecorationStyle: 'dotted', textDecorationThickness: 1.5, textDecorationColor: primaryColor }}><Emp>Real-time feedback</Emp> and after-practice <Emp>analysis</Emp> on
                    {' '}your <Accent>timing</Accent>, <Accent>dynamics</Accent>, <Accent>precision</Accent>,
                    {' '}and <Accent>unisons</Accent>.
                </Span>
                <Span fz={featSize}>
                    {' '}<Emp>Swing jazz</Emp> workouts
                    {' '}for Jazz, Fusion, Bebop, and Big Band.</Span>
                <Span fz={featSize}><Emp>World rhythms</Emp> workouts for Samba,
                    {' '}Songo, Mambo, Salsa (Cascara, Campana), Cha-Cha, Bossa Nova, Bembe,
                    {' '}Conga, Mozambique, Caribbean, Fusion, and more.</Span>
                <Span fz={featSize}><Emp>Linear</Emp> pattern workouts, plus <Emp>grooves &amp; fills studio</Emp>
                    {' '}where you can create <Emp>millions</Emp> of grooves &amp; fills with billions of variations.
                </Span>
                <Span fz={featSize}>
                    <Emp>300+</Emp> superb drumless music tracks to enjoy your practice.
                </Span>
                <Stack gap={'xs'}>
                    <Divider/>
                    <Span fz={'sm'}><Span c={primaryColor}>** Live feedback</Span> works on devices with a browser supporting
                        {' '}the WebMIDI standard. Please read
                        {' '}<Anchor fz={'inherit'} href={'/faq#what-do-i-need-to-use-the-live-feedback-feature'} target="_blank" rel="noreferrer">
                            What do I need to use the Live feedback feature?</Anchor> in the FAQ <Emp>before</Emp> you purchase a subscription.
                    </Span>
                </Stack>
            </Fragment>,
            stats: {
                ostinatos: foundation.stats!.ostinatos + 39 + 70,
                exercises: foundation.stats!.exercises + 1995 + 3730,
                variations: foundation.stats!.variations + 1138 + 2480, // TODO: update
                tracks: 303,
            }
        };

        const plans = [
            {
                plan: foundation,
                planPlus: foundationPlus
            },
            {
                plan: pro,
                planPlus: proPlus
            }];
        return plans;
    }, [primaryColor, featSize]);

    return result;
}
